/**
 * useRotateSshKey Hook
 *
 * Custom React Query mutation hook for rotating SSH key pairs for database connections.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { databaseConnectionService } from '../../../api/database-connection.service';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseRotateSshKeyOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for rotating SSH key pair for a database connection
 * @param tenantId - Tenant ID
 * @param dbConnectionId - Database Connection ID
 * @param options - Success/error callbacks
 */
export const useRotateSshKey = (
  tenantId: string,
  dbConnectionId: string,
  options?: UseRotateSshKeyOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => databaseConnectionService.rotateSshKey(tenantId, dbConnectionId),
    onSuccess: () => {
      // Invalidate both list and details queries to refresh SSH key data
      queryClient.invalidateQueries({ queryKey: ['database-connections', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['database-connections', tenantId, dbConnectionId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
